/**
 * MediaPlayerService - Global service to manage audio playback
 * Ensures only one media plays at a time across the application
 */

class MediaPlayerService {
  constructor() {
    this.currentPlayer = null;
    this.currentSheetId = null;
    this.isPlaying = false;
    this.listeners = new Map();
    this.youtubePlayer = null;
    this.youtubeReady = false;
    this.pendingPlay = false;
    
    // Initialize YouTube API
    this.initYouTubeAPI();
  }
  
  initYouTubeAPI() {
    // Check if API is already loaded
    if (window.YT && window.YT.Player) {
      this.youtubeReady = true;
      return;
    }
    
    // Load YouTube IFrame API
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    
    // Set up callback for when API is ready
    window.onYouTubeIframeAPIReady = () => {
      this.youtubeReady = true;
      console.log('YouTube API ready');
    };
  }
  
  /**
   * Extract video ID from YouTube URL
   */
  extractYouTubeId(url) {
    if (!url) return null;
    
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    
    return null;
  }
  
  /**
   * Extract start time parameter from YouTube URL
   * Supports both t=415 and start=415 formats
   */
  extractStartTime(url) {
    if (!url) return 0;
    
    // Check for start= parameter (seconds)
    const startMatch = url.match(/[?&]start=(\d+)/);
    if (startMatch) {
      return parseInt(startMatch[1], 10);
    }
    
    // Check for time format like t=1h30m45s or t=1m30s or t=90s
    const timeFormatMatch = url.match(/[?&]t=(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?(?:&|$)/);
    if (timeFormatMatch && (timeFormatMatch[1] || timeFormatMatch[2] || timeFormatMatch[3])) {
      const hours = parseInt(timeFormatMatch[1] || '0', 10);
      const minutes = parseInt(timeFormatMatch[2] || '0', 10);
      const seconds = parseInt(timeFormatMatch[3] || '0', 10);
      return hours * 3600 + minutes * 60 + seconds;
    }
    
    // Check for t= parameter (seconds only)
    const tMatch = url.match(/[?&]t=(\d+)(?:&|$)/);
    if (tMatch) {
      return parseInt(tMatch[1], 10);
    }
    
    return 0;
  }
  


  /**
   * Play media for a specific sheet
   */
  async play(sheetId, mediaUrl, containerId = null) {
    // If same sheet is already playing, pause it
    if (this.currentSheetId === sheetId && this.isPlaying) {
      return this.pause();
    }
    
    // If same sheet is paused, resume it
    if (this.currentSheetId === sheetId && !this.isPlaying && this.youtubePlayer) {
      return this.resume();
    }
    
    // Stop any currently playing media
    if (this.currentSheetId && this.currentSheetId !== sheetId) {
      await this.stop();
    }
    
    const videoId = this.extractYouTubeId(mediaUrl);
    if (!videoId) {
      console.error('Invalid YouTube URL:', mediaUrl);
      return false;
    }
    
    const startTime = this.extractStartTime(mediaUrl);
    
    // Set pending play flag to handle mobile autoplay restrictions
    this.pendingPlay = true;
    
    // Wait for YouTube API to be ready
    if (!this.youtubeReady) {
      await new Promise(resolve => {
        const checkReady = setInterval(() => {
          if (this.youtubeReady) {
            clearInterval(checkReady);
            resolve();
          }
        }, 100);
      });
    }
    
    // Create container for YouTube player if not provided
    if (!containerId) {
      // Create a hidden container for audio-only playback
      const container = document.createElement('div');
      container.id = `youtube-player-${sheetId}`;
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.width = '1px';
      container.style.height = '1px';
      document.body.appendChild(container);
      containerId = container.id;
    }
    
    // Create YouTube player
    this.youtubePlayer = new window.YT.Player(containerId, {
      height: '1',
      width: '1',
      videoId: videoId,
      playerVars: {
        autoplay: 0, // Never autoplay
        controls: 0,
        disablekb: 1,
        fs: 0,
        modestbranding: 1,
        rel: 0,
        showinfo: 0,
        start: startTime
      },
      events: {
        onReady: (event) => {
          console.log('YouTube player ready for sheet:', sheetId);
          this.currentSheetId = sheetId;
          
          // Notify ready state
          this.notifyListeners(sheetId, 'ready');
          
          // If we have a pending play request, try to start playing
          if (this.pendingPlay) {
            console.log('Starting playback after initialization (pending play)');
            this.pendingPlay = false;
            // Use a small delay to ensure the player is fully ready
            setTimeout(() => {
              try {
                event.target.playVideo();
              } catch (error) {
                console.log('Auto-play failed (expected on mobile), user will need to click again:', error);
              }
            }, 100);
          }
        },
        onStateChange: (event) => {
          console.log('YouTube player state change:', event.data, 'for sheet:', sheetId);
          
          if (event.data === window.YT.PlayerState.ENDED) {
            this.isPlaying = false;
            this.notifyListeners(sheetId, 'stopped');
          } else if (event.data === window.YT.PlayerState.PAUSED) {
            this.isPlaying = false;
            this.notifyListeners(sheetId, 'paused');
          } else if (event.data === window.YT.PlayerState.PLAYING) {
            this.isPlaying = true;
            this.notifyListeners(sheetId, 'playing');
          } else if (event.data === window.YT.PlayerState.BUFFERING) {
            // Still ready, just buffering
            this.notifyListeners(sheetId, 'ready');
          } else if (event.data === window.YT.PlayerState.CUED) {
            // Video is cued and ready to play
            this.notifyListeners(sheetId, 'ready');
          }
        },
        onError: (event) => {
          console.error('YouTube player error:', event.data, 'for sheet:', sheetId);
          this.notifyListeners(sheetId, 'error');
        }
      }
    });
    
    return true;
  }
  
  /**
   * Pause current media
   */
  pause() {
    if (this.youtubePlayer && this.youtubePlayer.pauseVideo) {
      this.youtubePlayer.pauseVideo();
      this.isPlaying = false;
      this.notifyListeners(this.currentSheetId, 'paused');
      return true;
    }
    return false;
  }
  
  /**
   * Resume playback
   */
  resume() {
    if (this.youtubePlayer && this.youtubePlayer.playVideo) {
      this.youtubePlayer.playVideo();
      this.isPlaying = true;
      this.notifyListeners(this.currentSheetId, 'playing');
      return true;
    }
    return false;
  }
  
  /**
   * Stop current media and cleanup
   */
  async stop() {
    if (this.youtubePlayer) {
      if (this.youtubePlayer.stopVideo) {
        this.youtubePlayer.stopVideo();
      }
      if (this.youtubePlayer.destroy) {
        this.youtubePlayer.destroy();
      }
      
      // Remove hidden container if it exists
      const container = document.getElementById(`youtube-player-${this.currentSheetId}`);
      if (container && container.parentNode) {
        container.parentNode.removeChild(container);
      }
      
      this.youtubePlayer = null;
    }
    
    const previousSheetId = this.currentSheetId;
    this.currentSheetId = null;
    this.isPlaying = false;
    
    if (previousSheetId) {
      this.notifyListeners(previousSheetId, 'stopped');
    }
  }
  
  /**
   * Check if a specific sheet is playing
   */
  isSheetPlaying(sheetId) {
    return this.currentSheetId === sheetId && this.isPlaying;
  }
  
  /**
   * Subscribe to playback state changes
   */
  subscribe(sheetId, callback) {
    if (!this.listeners.has(sheetId)) {
      this.listeners.set(sheetId, new Set());
    }
    this.listeners.get(sheetId).add(callback);
    
    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(sheetId);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          this.listeners.delete(sheetId);
        }
      }
    };
  }
  
  /**
   * Notify listeners of state changes
   */
  notifyListeners(sheetId, state) {
    const callbacks = this.listeners.get(sheetId);
    if (callbacks) {
      callbacks.forEach(callback => callback(state));
    }
    
    // Also notify 'all' listeners for global state changes
    const allCallbacks = this.listeners.get('all');
    if (allCallbacks) {
      allCallbacks.forEach(callback => callback({ sheetId, state }));
    }
  }
}

// Export singleton instance
const mediaPlayerService = new MediaPlayerService();
export default mediaPlayerService;